import {useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {zodResolver} from '@hookform/resolvers/zod'
import {CircleCheck, LoaderCircle} from 'lucide-react'
import {useForm} from 'react-hook-form'
import {z} from 'zod'
import {Button, Input, Label} from '@/shared/ui'
import * as authApi from '../api/authApi'

const schema = z
  .object({
    password: z.string().min(4, 'Минимум 4 символа'),
    confirmPassword: z.string().min(1, 'Повторите пароль'),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: 'Пароли не совпадают',
    path: ['confirmPassword'],
  })

type FormValues = z.infer<typeof schema>

interface ResetPasswordFormProps {
  resetToken: string
}

export function ResetPasswordForm({resetToken}: ResetPasswordFormProps) {
  const navigate = useNavigate()
  const [done, setDone] = useState(false)

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {password: '', confirmPassword: ''},
  })

  const onSubmit = form.handleSubmit(async data => {
    try {
      await authApi.resetPassword({reset_token: resetToken, new_password: data.password})
      setDone(true)
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Произошла ошибка. Попробуйте снова.'
      form.setError('root', {message})
    }
  })

  if (done) {
    return (
      <div className="flex flex-col items-center gap-4 text-center">
        <CircleCheck className="size-10 text-primary" strokeWidth={1.5} />
        <div>
          <h2 className="text-lg font-semibold">Пароль изменён</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Теперь вы можете войти с новым паролем.
          </p>
        </div>
        <Button size="lg" className="w-full" onClick={() => navigate('/login')}>
          Войти
        </Button>
      </div>
    )
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
      <div className="space-y-2">
        <Label htmlFor="reset-password">Новый пароль</Label>
        <Input
          id="reset-password"
          type="password"
          autoComplete="new-password"
          placeholder="••••••••"
          aria-invalid={Boolean(form.formState.errors.password)}
          {...form.register('password')}
        />
        {form.formState.errors.password && (
          <p className="text-sm text-destructive">{form.formState.errors.password.message}</p>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="reset-confirm">Повторите пароль</Label>
        <Input
          id="reset-confirm"
          type="password"
          autoComplete="new-password"
          placeholder="••••••••"
          aria-invalid={Boolean(form.formState.errors.confirmPassword)}
          {...form.register('confirmPassword')}
        />
        {form.formState.errors.confirmPassword && (
          <p className="text-sm text-destructive">
            {form.formState.errors.confirmPassword.message}
          </p>
        )}
      </div>
      {form.formState.errors.root && (
        <p className="text-sm text-destructive">{form.formState.errors.root.message}</p>
      )}
      <Button type="submit" size="lg" className="w-full" disabled={form.formState.isSubmitting}>
        {form.formState.isSubmitting ? (
          <>
            <LoaderCircle className="size-5 animate-spin" />
            Сохранение...
          </>
        ) : (
          'Сохранить пароль'
        )}
      </Button>
    </form>
  )
}
